import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UploadCloud } from 'lucide-react';
import { Lang } from '../types';
interface BookingFormProps {
  lang: Lang;
}
const content = {
  en: {
    title: 'Book Your Session',
    subtitle: 'Appointment Request — Barboxa Ink at Piajas Ink Tattoo Studio',
    instruction: 'Fill this out to get started:',
    fields: {
      name: 'Full Name',
      lang: 'Preferred Language',
      concept: 'Tattoo Concept / Idea',
      placement: 'Placement',
      size: 'Approximate Size',
      style: 'Style Preference',
      color: 'Color or Black & Grey',
      images: 'Reference Images',
      date: 'Ideal Session Date',
      contact: 'Instagram or Phone'
    },
    options: {
      size: ['Small', 'Medium', 'Large'],
      style: ['Fine Line', 'Minimal', 'Floral', 'Lettering', 'Other'],
      color: ['Color', 'B&G', 'Not sure']
    },
    uploadText: 'Upload 1–3 (optional)',
    submit: 'Request My Appointment at Piajas Ink',
    submitSub: "You'll hear back within 24–48 hours",
    success: "Request sent successfully. I'll be in touch soon."
  },
  es: {
    title: 'Reserva tu sesión',
    subtitle: 'Solicitud de cita — Barboxa Ink en Piajas Ink Tattoo Studio',
    instruction: 'Completa esto para comenzar:',
    fields: {
      name: 'Nombre completo',
      lang: 'Idioma preferido',
      concept: 'Concepto / Idea del tattoo',
      placement: 'Zona del cuerpo',
      size: 'Tamaño aproximado',
      style: 'Estilo',
      color: '¿Color o Black & Grey?',
      images: 'Imágenes de referencia',
      date: 'Fecha ideal',
      contact: 'Instagram o teléfono'
    },
    options: {
      size: ['Pequeño', 'Mediano', 'Grande'],
      style: ['Fine Line', 'Minimal', 'Floral', 'Lettering', 'Otro'],
      color: ['Color', 'B&G', 'No sé aún']
    },
    uploadText: 'Subir 1–3 (opcional)',
    submit: 'Solicitar mi cita en Piajas Ink',
    submitSub: 'Te respondo en 24–48 horas',
    success: 'Solicitud enviada con éxito. Me pondré en contacto pronto.'
  }
};
export function BookingForm({
  lang
}: BookingFormProps) {
  const t = content[lang];
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };
  return <section id="booking" className="py-12 md:py-16 bg-studio-bg" data-id="element-12">
      <div className="max-w-3xl mx-auto px-6" data-id="element-13">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-8" data-id="element-14">
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-15">{t.title}</h2>
          <h3 className="text-xl font-playfair text-studio-accent mb-2" data-id="element-16">
            {t.subtitle}
          </h3>
          <p className="text-studio-muted" data-id="element-17">{t.instruction}</p>
        </motion.div>

        {submitted ? <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="p-8 border border-studio-accent bg-studio-accent/10 text-center rounded-lg" data-id="element-18">
            <h3 className="text-2xl font-playfair text-studio-accent mb-2" data-id="element-19">
              Thank You / Gracias
            </h3>
            <p className="text-studio-text" data-id="element-20">{t.success}</p>
          </motion.div> : <motion.form initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} onSubmit={handleSubmit} className="space-y-6" data-id="element-21">
            <div className="grid md:grid-cols-2 gap-6" data-id="element-22">
              <div className="space-y-2" data-id="element-23">
                <label className="block text-sm text-studio-muted" data-id="element-24">
                  {t.fields.name}
                </label>
                <input required type="text" className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors" data-id="element-25" />
              </div>
              <div className="space-y-2" data-id="element-26">
                <label className="block text-sm text-studio-muted" data-id="element-27">
                  {t.fields.lang}
                </label>
                <select className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors appearance-none" data-id="element-28">
                  <option data-id="element-29">🇺🇸 English</option>
                  <option data-id="element-30">🇪🇸 Español</option>
                </select>
              </div>
            </div>

            <div className="space-y-2" data-id="element-31">
              <label className="block text-sm text-studio-muted" data-id="element-32">
                {t.fields.concept}
              </label>
              <textarea required rows={4} className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors resize-none" data-id="element-33"></textarea>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-34">
              <div className="space-y-2" data-id="element-35">
                <label className="block text-sm text-studio-muted" data-id="element-36">
                  {t.fields.placement}
                </label>
                <input required type="text" className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors" data-id="element-37" />
              </div>
              <div className="space-y-2" data-id="element-38">
                <label className="block text-sm text-studio-muted" data-id="element-39">
                  {t.fields.style}
                </label>
                <select className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors appearance-none" data-id="element-40">
                  {t.options.style.map(opt => <option key={opt} data-id="element-41">{opt}</option>)}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-42">
              <div className="space-y-3" data-id="element-43">
                <label className="block text-sm text-studio-muted" data-id="element-44">
                  {t.fields.size}
                </label>
                <div className="flex flex-col gap-2" data-id="element-45">
                  {t.options.size.map(opt => <label key={opt} className="flex items-center gap-3 cursor-pointer group" data-id="element-46">
                      <input type="radio" name="size" className="accent-studio-accent w-4 h-4" defaultChecked={opt === t.options.size[0]} data-id="element-47" />
                      <span className="text-studio-text group-hover:text-studio-accent transition-colors" data-id="element-48">
                        {opt}
                      </span>
                    </label>)}
                </div>
              </div>
              <div className="space-y-3" data-id="element-49">
                <label className="block text-sm text-studio-muted" data-id="element-50">
                  {t.fields.color}
                </label>
                <div className="flex flex-col gap-2" data-id="element-51">
                  {t.options.color.map(opt => <label key={opt} className="flex items-center gap-3 cursor-pointer group" data-id="element-52">
                      <input type="radio" name="color" className="accent-studio-accent w-4 h-4" defaultChecked={opt === t.options.color[0]} data-id="element-53" />
                      <span className="text-studio-text group-hover:text-studio-accent transition-colors" data-id="element-54">
                        {opt}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-55">
              <div className="space-y-2" data-id="element-56">
                <label className="block text-sm text-studio-muted" data-id="element-57">
                  {t.fields.date}
                </label>
                <input type="date" className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors [color-scheme:dark]" data-id="element-58" />
              </div>
              <div className="space-y-2" data-id="element-59">
                <label className="block text-sm text-studio-muted" data-id="element-60">
                  {t.fields.contact}
                </label>
                <input required type="text" className="w-full bg-studio-bgAlt border border-studio-border p-3 text-studio-text focus:border-studio-accent focus:outline-none transition-colors" data-id="element-61" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-62">
              <label className="block text-sm text-studio-muted" data-id="element-63">
                {t.fields.images}
              </label>
              <div className="border-2 border-dashed border-studio-border hover:border-studio-accent transition-colors p-8 text-center cursor-pointer bg-studio-bgAlt" data-id="element-64">
                <UploadCloud className="mx-auto mb-2 text-studio-muted" size={24} data-id="element-65" />
                <span className="text-studio-muted text-sm" data-id="element-66">
                  {t.uploadText}
                </span>
              </div>
            </div>

            <div className="pt-6 border-t border-studio-border" data-id="element-67">
              <button type="submit" className="w-full group relative inline-flex items-center justify-center px-8 py-4 bg-studio-accent text-studio-bg font-semibold tracking-wide uppercase overflow-hidden transition-transform hover:scale-[1.01] active:scale-[0.99]" data-id="element-68">
                <span className="relative z-10 flex items-center gap-2" data-id="element-69">
                  📅 {t.submit}
                </span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-70" />
              </button>
              <p className="text-center text-sm text-studio-muted mt-4 italic" data-id="element-71">
                *{t.submitSub}
              </p>
            </div>
          </motion.form>}
      </div>
    </section>;
}